import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button, Icon, Divider, Message } from "semantic-ui-react";
import { Route, Switch, Link, Redirect } from "react-router-dom";
import { getUsers } from "store/selectors";
import PageWrap from "components/PageWrap/PageWrap";
import { CampaignsTable } from "components/CampaignsTable/CampaignsTable";
import LoadResolver from "components/LoadResolver";
import CampaignForm from "forms/Campaign";
import CampaignDisplay from "components/CampaignDisplay/CampaignDisplay";
import CSVCampaignsForm from "forms/CSVCampaigns";
import { MergeCampaignsForm } from "forms/MergeCampaigns";
import { STEPS } from "store/models/campaignsPage";
import Campaigns from "components/Campaigns/Campaigns";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import DeliveryUpload from "components/DeliveryUpload/DeliveryUpload";

// Maximum number of campaigns that can be merged into one campaign
const MAX_NR_OF_MERGE_CAMPAIGNS = 2;

function CampaignsPage(props) {
  const { match } = props;
  const { t } = useTranslation("common");
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch.campaignsPage.resetWholeState();
    dispatch.users.init();
    return dispatch.deliveries.reset;
  }, []);

  const [onlyInactiveCampaigns, setOnlyInactiveCampaigns] = useState(null);

  const { step, campaignsToBeMerged, assetActions, campaign } = useSelector(
    (state) => state.campaignsPage
  );

  const areCampaignsToBeMergedSameType = () => {
    const type = campaignsToBeMerged[0] && campaignsToBeMerged[0].type;
    if (!type) return false;
    // Check if every campaign has the same type as the first one
    return _.every(_.map(campaignsToBeMerged), ["type", type]);
  };

  const content = () => {
    switch (step) {
      case STEPS.INIT:
      default:
        return (
          <div className="page-container large">
            <Button
              primary
              onClick={dispatch.campaignsPage.onCreateCampaign}
              data-test="campaigns_page_create_new_button"
            >
              {t("campaigns_page_create_new")}
            </Button>
            <Button primary onClick={dispatch.campaignsPage.onCreateFromCSV}>
              {t("campaigns_page_create_CSV")}
            </Button>
            <Button
              primary
              onClick={dispatch.campaignsPage.onMergeCampaigns}
              disabled={
                // !campaignCount ||
                campaignsToBeMerged.length < MAX_NR_OF_MERGE_CAMPAIGNS ||
                !areCampaignsToBeMergedSameType()
              }
            >
              {t("campaigns_page_merge_campaigns")}
            </Button>
            {campaignsToBeMerged.length > 0 && (
              <>
                <h3>{t("campaigns_page_selected_for_merging")} </h3>
                <Campaigns
                  groups={campaignsToBeMerged}
                  key={"mergeCampaigns"}
                  accordionKey={100}
                  staticValues
                  campaignsForMerge={true}
                  t={t}
                />
                {campaignsToBeMerged.length === MAX_NR_OF_MERGE_CAMPAIGNS &&
                  !areCampaignsToBeMergedSameType() && (
                    <Message error>{t("campaigns_page_error_merge_type")}</Message>
                  )}
                <Divider />
              </>
            )}
            <CampaignsTable
              onlyInactiveCampaigns={!!onlyInactiveCampaigns}
              maxNrOfMergeCampaigns={MAX_NR_OF_MERGE_CAMPAIGNS}
              dispatch={dispatch}
              t={t}
            />
          </div>
        );
      case STEPS.CREATE_CAMPAIGN:
        return (
          <div className="page-container">
            <CampaignForm
              onSuccess={dispatch.campaignsPage.reset}
              goPrev={dispatch.campaignsPage.reset}
            />
          </div>
        );
      case STEPS.CREATE_FROM_CSV:
        return (
          <div className="page-container">
            <CSVCampaignsForm
              onSuccess={dispatch.campaignsPage.reset}
              goPrev={dispatch.campaignsPage.reset}
            />
          </div>
        );
      case STEPS.MERGE_CAMPAIGNS:
        return (
          <div className="page-container">
            <MergeCampaignsForm
              campaignsToBeMerged={campaignsToBeMerged}
              onSuccessRunQC={dispatch.campaignsPage.onRunQC}
              onSuccessRedirect={dispatch.campaignsPage.onCampaignRedirect}
              goPrev={dispatch.campaignsPage.reset}
              dispatch={dispatch}
              t={t}
            />
          </div>
        );
      case STEPS.RUN_QC:
        return (
          <div className="page-container">
            <DeliveryUpload actions={assetActions} campaign={campaign} />
          </div>
        );
      case STEPS.REDIRECT_TO_CAMPAIGN:
        return <Redirect to={`/campaigns/${campaign.id}`} />;
    }
  };

  const Root = ({ inactiveCampaigns }) => {
    useEffect(() => setOnlyInactiveCampaigns(!!inactiveCampaigns), [inactiveCampaigns]);
    if (onlyInactiveCampaigns !== null) return <div key={step}>{content()}</div>;
    return null;
  };

  const ShowCampaign = (props) => {
    const { campaignId } = props.match.params;

    const activeUser = useSelector((state) => state.activeUser.item) || {};
    const isAdmin = activeUser.isAdmin || activeUser.isSuperAdmin || activeUser.isPowerAdmin;
    const users = useSelector(getUsers);

    return (
      <LoadResolver isLoading={users.isLoading} error={users.error} hideWhenLoading>
        <div className="page-container">
          <Link
            data-test="campaigns_page_back_link"
            to={onlyInactiveCampaigns ? "/campaigns/inactive-campaigns" : "/campaigns"}
            style={{ fontSize: "1.25rem", fontWeight: "600", lineHeight: "3rem", color: "#4700a3" }}
            onClick={dispatch.campaignsPage.reset}
          >
            <Icon name="angle left"></Icon>
            {t("campaigns_page_back_link")}
          </Link>
          <CampaignDisplay
            campaignId={campaignId}
            users={users.items}
            dispatch={dispatch}
            activeUser={activeUser}
            isAdmin={isAdmin}
          />
        </div>
      </LoadResolver>
    );
  };
  return (
    <PageWrap page="campaigns" t={t} dispatch={dispatch}>
      <div key={props.location.key}>
        <Switch location={props.location}>
          <Route exact path={match.path} render={() => <Root />} />
          <Route
            exact
            path={match.path + "/inactive-campaigns"}
            render={() => <Root inactiveCampaigns />}
          />
          <Route path={match.path + "/:campaignId"} component={ShowCampaign} />
        </Switch>
      </div>
    </PageWrap>
  );
}

export default CampaignsPage;
